/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import uniq from 'lodash.uniq';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {BaseAsset} from '@ion/core/src/types/entities';
import {toMap} from '@ion/core/src/utils/ObjectUtil';
import GlobalGroupsSearch from '@ion/features/src/global-groups/search';
import {CachedData, RequestTreeDataType} from '../types/types';

export interface TreeItem {
  id: string;
  assetId: string;
  title: string;
  isAsset: boolean;
  isDevice?: boolean;
}

/**
 * createSelectedGroups filters only groups from the checkedKeys of treeFilter
 *
 */
export const createSelectedGroups = async({
  checkedKeys,
  cachedData= {},
  groupTreeFiltersRef = null
}) => {
  const validKeys = checkedKeys?.filter(Boolean);
  const storedItemKeys = cachedData? Object.values(cachedData).map(({key}) => key) : [];
  // filter checkedKeys with groups only
  const checkedKeysGroupOnly = validKeys.filter(key => {
    const isPhantomNode = key[0] === '-';
    if (isPhantomNode) {
      const parentKey = key.slice(1);
      // remove the phantom node
      return !validKeys.find(itemKey => itemKey === parentKey || itemKey === key);
    }
    return !storedItemKeys.find(itemKey => itemKey === key);
  });
  const checkedGroups =  await groupTreeFiltersRef?.current?.getGroupsForCheckedKeys(checkedKeysGroupOnly);
  if (checkedGroups.length) {
    return checkedGroups.reduce((acc, item) => {
      if (item) {
        const {title, id} = item;
        acc.push({
          name : title,
          id
        });
      }
      return acc;
    }, []);
  }
  return [];
};

/**
 * createSelectedData filters only targets from the checkedKeys of treeFilter
 *
 */
export const createSelectedData = (
  checkedKeys: string[] = [],
  cachedData: CachedData = {},
  target: string = null
) => {
  const map = toMap(checkedKeys, 'key');
  return Object.values(cachedData)
    .filter(data => data[target] && map[data?.key])
    .map(({id, title: name, accountId}) => ({id, name, accountId}));
};

/**
 * getSelectedCounts returns a count of selected groups + assets
 *
 * @param groupsFilterState groups property of filterState
 * @param assetsFilterState assets property of filterState
 * @returns
 */
export const getSelectedCounts = (groupsFilterState, assetsFilterState, deviceFilterState) => {
  // remove assets under group has been checked
  const reducedAssets = assetsFilterState?.selected?.filter(({accountId}) =>
    !groupsFilterState?.selected?.find(({id}) => id === accountId)
  );
  // remove devices under group has been checked
  const reducedDevices = deviceFilterState?.selected?.filter(({accountId}) =>
    !groupsFilterState?.selected?.find(({id}) => id === accountId)
  );
  // remove phantom node if both parent and phantom node are selected.
  const uniqGroups = uniq(groupsFilterState?.selected?.map(({id}) => id));
  const assetsCount = Number.isNaN(reducedAssets?.length)? 0 : reducedAssets?.length;
  const deviceCount = Number.isNaN(reducedDevices?.length)? 0 : reducedDevices?.length;
  const groupsCount = uniqGroups?.length ?? 0;
  let count = assetsCount + groupsCount;
  count = deviceCount ? count + deviceCount : count;
  return count > 0 ? count : null;
};

export const getId = (item: BaseAsset) => {
  const {account, accountId, assetId, id} = item || {};
  return {
    assetId : assetId > 0 ? assetId?.toString() : id?.toString(),
    id      : accountId?.toString() || entityId(account)?.toString()
  };
};

/**
 * checkIsDevice returns boolean value true or false based on the isScDevice, assetName.
 * Will return true if the selected value is device else false if it is asset.
 *
 * ```typescript
 * isDevice = checkIsDevice(true, undefined); // will return true as it is a device.
 * ...
 *
 * ```
 *
 * @param isScDevice The flag indicates device is SC device or not
 * @param assetName  represents the name of the selected asset
 * @returns
 */
export const checkIsDevice = (isScDevice, assetName) => {
  if (isScDevice) {
    return !assetName;
  }
  return false;
};

export const normalize = (item: BaseAsset): TreeItem => {
  const {assetName, name, deviceName, isScDevice, isCvsAsset} = item || {};
  const nameUI        = assetName || name;
  const {assetId, id} = getId(item);
  const isDevice      = checkIsDevice(isScDevice, nameUI);
  const idKey         = isDevice ? 'deviceId' : 'assetId';
  return {
    [idKey] : assetId,
    title   : isDevice ? deviceName : nameUI,
    isAsset : !!nameUI,
    assetId,
    isDevice,
    id
  };
};

/**
 * utlity to provide mapped to determine if the data input is
 * assets or devices and returns in CachedItem format
 *
 * ```typescript
 * import {mapDataToGroups} from '@ion/dls/src/filters/TreeFilterUtils';
 *
 *  const devicesFound = mapDataToGroups({
 *    data: devices, type: RequestTreeDataType.Devices
 *    });
 * ...
 *
 * ```
 * @param {Device[]} data: assets/ devices respons from CTC service
 * @param {RequestTreeDataType} type: type of request made prior to call this method
 *
 */
export const mapDataToGroups = ({data, type}) => {
  return data?.map(({name, id, account}) => {
    const accountId = entityId(account)?.toString();
    const isAsset   = type === RequestTreeDataType.Assets;
    // return asset/ devices to include its own assetId and global group ID as id
    const results = {title: name, id: accountId};
    if (isAsset) {
      return {...results, assetId: id, isAsset};
    }
    return {...results, deviceId: id, isDevice: true};
  });
};

/**
 * utility to provide id of item selected from the options displayed
 * after search in the tree filter
 *
 * ```typescript
 * import {getOptionId} from '@ion/dls/src/filters/TreeFilterUtils';
 *
 * const targetId = getOptionId(optionSelected);
 *
 * ...
 *
 * @param optionSelected : option selected from the search result in the tree filter
 */
export const getOptionId = ({isAsset, assetId, deviceId}) => {
  return isAsset ? assetId : deviceId;
};
